/**
 * Travel Modal
 * 
 * Modal dialog that appears when user clicks on a distant hex,
 * showing route information and allowing confirmation or cancellation
 */

import React from 'react';
import ReactDOM from 'react-dom';
import './TravelModal.css';
import { AxialCoord, TerrainType } from '../types/terrain';
import { TERRAIN_NAMES } from '../types/terrain';

interface TravelModalProps {
  destination: AxialCoord;
  destinationTerrain: TerrainType;
  routeDistance: number; // Number of hexes in route
  staminaCost: number;
  travelTime: number; // Time in milliseconds
  playerStamina: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function TravelModal({
  destination,
  destinationTerrain,
  routeDistance,
  staminaCost,
  travelTime,
  playerStamina,
  onConfirm,
  onCancel,
}: TravelModalProps) {
  const hasSufficientStamina = playerStamina >= staminaCost;
  const movementHexes = routeDistance - 1; // Don't count starting hex
  
  // Format travel time for display
  const formatTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  };

  return ReactDOM.createPortal(
    <div className="travel-modal-overlay" onClick={onCancel}>
      <div className="travel-modal" onClick={(e) => e.stopPropagation()}>
        <div className="travel-modal-header">
          <h2>🗺️ Plan Journey</h2>
          <button className="travel-modal-close" onClick={onCancel}>×</button>
        </div>
        
        <div className="travel-modal-content">
          <div className="travel-modal-destination">
            <div className="travel-modal-label">Destination</div>
            <div className="travel-modal-value">
              {TERRAIN_NAMES[destinationTerrain]} ({destination.q}, {destination.r})
            </div>
          </div>

          <div className="travel-modal-stats">
            <div className="travel-modal-stat">
              <div className="travel-modal-stat-label">Distance</div>
              <div className="travel-modal-stat-value">
                {movementHexes} hex{movementHexes !== 1 ? 'es' : ''}
              </div>
            </div>

            <div className="travel-modal-stat">
              <div className="travel-modal-stat-label">Stamina Cost</div>
              <div className={`travel-modal-stat-value ${!hasSufficientStamina ? 'travel-modal-stat-insufficient' : ''}`}>
                {staminaCost} / {playerStamina}
              </div>
            </div>

            <div className="travel-modal-stat">
              <div className="travel-modal-stat-label">Travel Time</div>
              <div className="travel-modal-stat-value">
                {formatTime(travelTime)}
              </div>
            </div>
          </div>

          {!hasSufficientStamina && (
            <div className="travel-modal-warning">
              ⚠️ Insufficient stamina! You need {staminaCost - playerStamina} more stamina to make this journey.
              <div className="travel-modal-warning-hint">
                Consume rations or rest to restore stamina.
              </div>
            </div>
          )}

          {hasSufficientStamina && (
            <div className="travel-modal-info">
              💡 You may encounter monsters along the way. Your journey can be cancelled at any time, but stamina will not be refunded.
            </div>
          )}
        </div>

        <div className="travel-modal-actions">
          <button 
            className="travel-modal-button travel-modal-button-cancel" 
            onClick={onCancel}
          >
            Cancel
          </button>
          {hasSufficientStamina && (
            <button 
              className="travel-modal-button travel-modal-button-confirm" 
              onClick={onConfirm}
            >
              Begin Journey
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

